// Push Notification Hook - Manages service worker registration and push subscriptions

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Check if push notifications are supported
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// Get current permission status
export function getPermissionStatus() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission; // 'default', 'granted', 'denied'
}

// Register service worker
export async function registerServiceWorker() {
  if (!isPushSupported()) {
    console.log('Push notifications not supported');
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    console.log('Service Worker registered:', registration.scope);
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
}

// Get VAPID public key from server
export async function getVapidPublicKey() {
  try {
    const response = await fetch(`${API_URL}/api/platform-settings`, {
      credentials: 'include'
    });
    
    if (!response.ok) return null;
    
    const settings = await response.json();
    return settings?.web_push?.vapid_public_key || null;
  } catch (error) {
    console.error('Failed to get VAPID key:', error);
    return null;
  }
}

// Convert base64 to Uint8Array for applicationServerKey
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Subscribe to push notifications
export async function subscribeToPush(vapidPublicKey) {
  if (!isPushSupported()) {
    throw new Error('Push notifications not supported');
  }
  
  // Request permission
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission denied');
  }
  
  // Get service worker registration
  const registration = await navigator.serviceWorker.ready;
  
  // Subscribe to push
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
  });
  
  // Send subscription to server
  const response = await fetch(`${API_URL}/api/notifications/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      endpoint: subscription.endpoint,
      keys: {
        p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
        auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
      }
    })
  });
  
  if (!response.ok) {
    throw new Error('Failed to save subscription');
  }
  
  return subscription;
}

// Unsubscribe from push notifications
export async function unsubscribeFromPush() {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  
  if (subscription) {
    await subscription.unsubscribe();
    
    // Notify server
    await fetch(`${API_URL}/api/notifications/unsubscribe`, {
      method: 'DELETE',
      credentials: 'include'
    });
  }
}

// Check if already subscribed
export async function isSubscribed() {
  if (!isPushSupported()) return false;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch {
    return false;
  }
}

// Initialize push notifications (call on app load)
export async function initializePushNotifications() {
  if (!isPushSupported()) {
    console.log('Push notifications not supported in this browser');
    return { supported: false };
  }
  
  // Register service worker
  const registration = await registerServiceWorker();
  if (!registration) {
    return { supported: true, registered: false };
  }
  
  // Check current subscription status
  const subscribed = await isSubscribed();
  const permission = getPermissionStatus();
  
  return {
    supported: true,
    registered: true,
    subscribed,
    permission
  };
}
